import React, { createContext, useContext } from 'react';
import { Tabs as AriaTabs, type Key } from 'react-aria-components';
import { cva, type VariantProps } from 'class-variance-authority';
import { TabPanel } from './TabPanel';
import styles from './Tabs.module.css';
import { AltoError } from '../../utils/console/error';

const tabs = cva(styles.tabs, {
  variants: {
    styleVariant: {
      line: styles['tabs--line'],
      pill: styles['tabs--pill'],
      contained: styles['tabs--contained'],
      heading: styles['tabs--heading'],
    },
    align: {
      start: styles['tabs--align-start'],
      center: styles['tabs--align-center'],
    },
  },
  defaultVariants: {
    styleVariant: 'line',
    align: 'start',
  },
});

export interface TabsProps extends VariantProps<typeof tabs> {
  /**
   * Tab components and panels OR routes content
   */
  children?: React.ReactNode;
  /**
   * Visual style variant of the tabs
   */
  styleVariant?: 'line' | 'pill' | 'contained' | 'heading';
  /**
   * Horizontal alignment of tab list
   */
  align?: 'start' | 'center';
  /**
   * Whether tabs should fill the available width
   */
  fillContainer?: boolean;
  /**
   * Focus outline color style for accessibility
   */
  focusStyle?: 'default' | 'white';
  /**
   * Heading level for heading variant tabs
   */
  headingLevel?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Currently selected tab key for controlled component
   */
  selectedKey?: Key;
  /**
   * Default selected tab key for uncontrolled component
   */
  defaultSelectedKey?: Key;
  /**
   * Array of tab keys that should be disabled
   */
  disabledKeys?: Key[];
  /**
   * Callback fired when a tab is selected
   */
  onSelectionChange?: (key: Key) => void;
  /**
   * Route content to display when using tabs as navigation links
   * When provided, replaces individual TabPanel children with a single routing container
   * Typically used with React Router or similar routing solutions
   */
  routes?: React.ReactNode;
  /**
   * Test ID for the component, used for testing purposes
   */
  'data-testid'?: string;
}

export interface TabsContextValue {
  styleVariant: 'line' | 'pill' | 'contained' | 'heading';
  align: 'start' | 'center';
  fillContainer: boolean;
  focusStyle: 'default' | 'white';
  headingLevel: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const TabsContext = createContext<TabsContextValue | null>(null);

export const useTabsContext = (): TabsContextValue => {
  const context = useContext(TabsContext);
  if (!context) {
    throw AltoError('Tab components must be used within the `Tabs` component', {
      scope: 'Tabs',
    });
  }
  return context;
};

export const Tabs: React.FC<TabsProps> = ({
  children,
  styleVariant = 'line',
  align = 'start',
  fillContainer = false,
  focusStyle = 'default',
  headingLevel = 'h3',
  selectedKey,
  defaultSelectedKey,
  disabledKeys,
  onSelectionChange,
  routes,
  'data-testid': dataTestId,
}) => {
  const contextValue: TabsContextValue = {
    styleVariant,
    align,
    fillContainer,
    focusStyle,
    headingLevel,
  };

  return (
    <TabsContext.Provider value={contextValue}>
      <AriaTabs
        className={tabs({ styleVariant, align })}
        selectedKey={selectedKey}
        defaultSelectedKey={defaultSelectedKey}
        disabledKeys={disabledKeys}
        onSelectionChange={onSelectionChange}
        data-testid={dataTestId}
      >
        {children}

        {/* Conditional rendering: either routes or individual panels */}
        {routes && selectedKey && (
          <TabPanel id={selectedKey} className={styles.tabs__panel}>
            {routes}
          </TabPanel>
        )}
      </AriaTabs>
    </TabsContext.Provider>
  );
};

// Export subcomponents
export { TabList } from './TabList';
export { Tab } from './Tab';
export { TabPanel } from './TabPanel';

export default Tabs;
